import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import {
	CLASSIFICATION_LABELS,
	CLASSIFICATION_ORDER,
	COFFEE_LABELS,
	CROP_STAGE_LABELS,
	IRRIGATION_LABELS,
	PARAM_LABELS,
	PLAN_SECTION_LABELS,
	PLANTING_FORM_LABELS,
	STATE_SECTION_LABELS,
} from '../constants/parameters.constants';
import type {
	CoffeeType,
	FertilizationPlanReferenceDTO,
	PlanCropStage,
	PlanIrrigationSystem,
	SoilGroupKey,
	StateReferenceDTO,
	StateReferenceFixedClass,
} from '../types/parameters.types';
import { applyScale, formatNumber } from './format';
import { listRangeCombinations } from './rangeCombinations';
import { buildSoilCards } from './soilCards';

interface ExportTable {
	title: string;
	headers: string[];
	rows: (string | number)[][];
}

const SOIL_GROUPS: SoilGroupKey[] = ['macro', 'micro', 'indexes'];

const formatClassLimits = (item: StateReferenceFixedClass | undefined) => {
	if (!item) return '—';
	if (item.minValue === undefined) return `< ${formatNumber(item.maxValue)}`;
	if (item.maxValue === undefined) return `> ${formatNumber(item.minValue)}`;
	return `${formatNumber(item.minValue)} – ${formatNumber(item.maxValue)}`;
};

const buildSoilTable = (reference: StateReferenceDTO): ExportTable => ({
	title: 'Solo',
	headers: [
		'Parâmetro',
		'Código',
		'Regra',
		'Unidade',
		'Método',
		'Faixa ideal',
		...CLASSIFICATION_ORDER.map((classification) => CLASSIFICATION_LABELS[classification]),
	],
	rows: SOIL_GROUPS.flatMap((group) => buildSoilCards(reference, group)).map((card) => [
		card.title,
		card.code,
		card.meta,
		card.unit ?? '',
		card.method ?? '',
		card.idealRange ?? '',
		...CLASSIFICATION_ORDER.map((classification) =>
			formatClassLimits(card.classes.find((item) => item.classification === classification))
		),
	]),
});

const buildLimingTable = (reference: StateReferenceDTO): ExportTable => ({
	title: STATE_SECTION_LABELS.liming,
	headers: ['Cultura', 'Parâmetro', 'Valor'],
	rows: reference.liming.flatMap((item) =>
		item.parameters.map((parameter) => [
			COFFEE_LABELS[item.coffeeType] ?? item.coffeeType,
			PARAM_LABELS[parameter.code] ?? parameter.code,
			formatNumber(parameter.value),
		])
	),
});

const buildPlasteringTable = (reference: StateReferenceDTO): ExportTable => ({
	title: STATE_SECTION_LABELS.plastering,
	headers: ['Parâmetro', 'Valor'],
	rows: reference.plastering.map((parameter) => [
		PARAM_LABELS[parameter.code] ?? parameter.code,
		formatNumber(parameter.value),
	]),
});

const buildP2O5Table = (reference: StateReferenceDTO): ExportTable => ({
	title: STATE_SECTION_LABELS.p2o5,
	headers: ['Forma de plantio', 'Classificação', 'Dose de referência'],
	rows: reference.demandP2O5.flatMap((item) =>
		item.fixedClasses.map((dose) => [
			PLANTING_FORM_LABELS[item.form] ?? item.form,
			CLASSIFICATION_LABELS[dose.classification],
			formatNumber(dose.maxValue),
		])
	),
});

const buildFoliarTable = (reference: StateReferenceDTO): ExportTable => ({
	title: STATE_SECTION_LABELS.foliar,
	headers: ['Cultura', 'Contexto', 'Nutriente', 'Limite 1', 'Limite 2', 'Limite 3', 'Limite 4'],
	rows: listRangeCombinations(reference.criticalRanges, 'criticalRanges').flatMap((combination) =>
		combination.ranges.map((range) => [
			combination.title,
			combination.context,
			PARAM_LABELS[range.code] ?? range.code,
			...range.range.map((value) => formatNumber(value)),
		])
	),
});

const buildDrisTable = (reference: StateReferenceDTO): ExportTable => ({
	title: STATE_SECTION_LABELS.dris,
	headers: ['Cultura', 'Contexto', 'Relação', 'Média', 'Desvio padrão'],
	rows: listRangeCombinations(reference.drisParams, 'drisParams').flatMap((combination) =>
		combination.ranges.map((range) => [
			combination.title,
			combination.context,
			range.code.replace('_', ' / '),
			...range.range.map((value) => formatNumber(value)),
		])
	),
});

const buildExportTables = (reference: StateReferenceDTO): ExportTable[] =>
	[
		buildSoilTable(reference),
		buildLimingTable(reference),
		buildPlasteringTable(reference),
		buildP2O5Table(reference),
		buildFoliarTable(reference),
		buildDrisTable(reference),
	].filter((table) => table.rows.length > 0);

const buildPlanTables = (plan: FertilizationPlanReferenceDTO): ExportTable[] => {
	const nutrients = plan.nutrientOrder;
	const coffees = Object.keys(COFFEE_LABELS) as CoffeeType[];
	const stages = Object.keys(CROP_STAGE_LABELS) as PlanCropStage[];
	const percent = (value: number) => `${formatNumber(applyScale(value, 100), 2)}%`;
	const soil = plan.soilNutrientEfficacyByIrrigationType;
	const fertilizer = plan.fertilizerNutrientEfficacyByIrrigationType;

	return [
		{
			title: PLAN_SECTION_LABELS.root,
			headers: ['Tipo de café', ...stages.map((stage) => CROP_STAGE_LABELS[stage])],
			rows: coffees.map((coffee) => [
				COFFEE_LABELS[coffee],
				...stages.map((stage) => formatNumber(plan.rootFactorByCultureAndCropStage[coffee][stage])),
			]),
		},
		{
			title: PLAN_SECTION_LABELS.extraction,
			headers: ['Tipo de café', 'Fase', ...nutrients],
			rows: coffees.flatMap((coffee) =>
				stages.map((stage) => [
					COFFEE_LABELS[coffee],
					CROP_STAGE_LABELS[stage],
					...plan.nutrientExtractionByCultureAndCropStage[coffee][stage].map((value) => formatNumber(value)),
				])
			),
		},
		{
			title: PLAN_SECTION_LABELS.exports,
			headers: ['Tipo de café', ...nutrients],
			rows: coffees.map((coffee) => [
				COFFEE_LABELS[coffee],
				...plan.nutrientExportsByCulture[coffee].map((value) => formatNumber(value)),
			]),
		},
		{
			title: PLAN_SECTION_LABELS.efficiency,
			headers: ['Origem', 'Sistema', ...nutrients],
			rows: [
				...(Object.keys(soil) as PlanIrrigationSystem[]).map((system) => [
					'Solo',
					IRRIGATION_LABELS[system],
					...soil[system].map((value) => formatNumber(value)),
				]),
				...(Object.keys(fertilizer) as (PlanIrrigationSystem | 'broad')[]).map((system) => [
					'Fertilizante',
					IRRIGATION_LABELS[system],
					...fertilizer[system].map(percent),
				]),
			],
		},
		{
			title: PLAN_SECTION_LABELS.minimum,
			headers: ['Nutriente', 'Fração mínima'],
			rows: nutrients.map((nutrient) => [
				PARAM_LABELS[nutrient] ?? nutrient,
				percent(plan.nutrientMinimumFraction[nutrient]),
			]),
		},
	];
};

const fileName = (name: string, extension: string) =>
	`parametros-${name.replace(/\s+/g, '-').toLowerCase()}.${extension}`;

const writeExcel = (name: string, tables: ExportTable[]) => {
	const workbook = XLSX.utils.book_new();
	tables.forEach((table) => {
		const sheet = XLSX.utils.aoa_to_sheet([[name], [], table.headers, ...table.rows]);
		XLSX.utils.book_append_sheet(workbook, sheet, table.title.slice(0, 31));
	});
	XLSX.writeFile(workbook, fileName(name, 'xlsx'));
};

const writePdf = (name: string, tables: ExportTable[]) => {
	const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
	doc.setFontSize(16);
	doc.text(`Parâmetros - ${name}`, 14, 15);

	let startY = 22;
	tables.forEach((table) => {
		doc.setFontSize(12);
		doc.text(table.title, 14, startY);
		autoTable(doc, {
			startY: startY + 4,
			head: [table.headers],
			body: table.rows,
			styles: { fontSize: 7, cellPadding: 1.5 },
			headStyles: { fillColor: [69, 58, 241], textColor: 255, fontStyle: 'bold' },
			alternateRowStyles: { fillColor: [245, 245, 245] },
		});
		startY = (doc as jsPDF & { lastAutoTable: { finalY: number } }).lastAutoTable.finalY + 10;
		if (startY > 180) {
			doc.addPage();
			startY = 20;
		}
	});

	doc.save(fileName(name, 'pdf'));
};

export const exportParametersToExcel = (reference: StateReferenceDTO) =>
	writeExcel(reference.referenceCode, buildExportTables(reference));

export const exportParametersToPdf = (reference: StateReferenceDTO) =>
	writePdf(reference.referenceCode, buildExportTables(reference));

export const exportPlanToExcel = (plan: FertilizationPlanReferenceDTO) => writeExcel(plan.name, buildPlanTables(plan));

export const exportPlanToPdf = (plan: FertilizationPlanReferenceDTO) => writePdf(plan.name, buildPlanTables(plan));
